/**
 * Printing with the cloud away (08-clients.md "Device bridge", F10 step 3): KOTs and bills
 * are rendered on the device from the same templates the server uses
 * (`@qrkey/domain` print-templates) and sent
 *
 *   1. through this device's own DeviceBridge when the printer is attached to this terminal;
 *   2. else to the outlet hub, which prints it (its own printers) or forwards it to the
 *      terminal that owns the printer;
 *   3. else kept in a persisted queue and tried again (`flushPrintQueue`) when a hub shows
 *      up, the printer comes back or the cloud returns.
 *
 * A job has a stable id (`kot:{kotId}:{printer}`, `bill:{order}:{n}`): a job printed
 * on this device is remembered and never printed twice, and the hub deduplicates by the
 * same id, so retries and re-sends are safe.
 */
import type { OrderView, OutletBootstrap } from '@qrkey/api-client';
import type { PrintDoc } from '@qrkey/contracts';
import type { DeviceBridge, PrinterRef } from '@qrkey/device-bridge';
import {
  formatOutletTime,
  gstinStateCode,
  renderBillDoc,
  renderKotDoc,
  type BillPrintData,
} from '@qrkey/domain';
import { terminalStore } from '../app/terminal.js';
import { kvGet, kvSet } from './db.js';
import { resolveHubClient, type HubLinkDeps } from './hub-link.js';
import { buildKotPrints, type SendFacts } from './kitchen.js';

type Printer = OutletBootstrap['printers'][number];

export interface PendingPrint {
  id: string;
  outletId: string;
  printerId: string;
  printer: Pick<Printer, 'id' | 'connection' | 'address' | 'paperMm' | 'ownerTerminalId'>;
  doc: PrintDoc;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

export type PrintRoute = 'device' | 'hub' | 'queued';

const QUEUE_KEY = 'print:queue';
const DONE_KEY = 'print:done';
const MAX_DONE = 500;

export interface PrintDeps extends HubLinkDeps {
  bridge?: DeviceBridge | undefined;
}

const refOf = (p: PendingPrint['printer']): PrinterRef => ({
  printerId: p.id,
  connection: p.connection,
  address: p.address,
  paperMm: p.paperMm,
});

async function done(): Promise<string[]> {
  return (await kvGet<string[]>(DONE_KEY)) ?? [];
}

async function markDone(id: string): Promise<void> {
  const ids = await done();
  if (!ids.includes(id)) await kvSet(DONE_KEY, [...ids, id].slice(-MAX_DONE));
}

export async function queuedPrints(): Promise<PendingPrint[]> {
  return (await kvGet<PendingPrint[]>(QUEUE_KEY)) ?? [];
}

async function setQueue(queue: PendingPrint[]): Promise<void> {
  await kvSet(QUEUE_KEY, queue);
}

/** Try the routes in order; returns where the job went, or null when none took it. */
async function attempt(
  job: PendingPrint,
  deps: PrintDeps,
): Promise<{ route: PrintRoute } | { error: string }> {
  const me = terminalStore.get()?.terminalId ?? null;
  let error = 'No printer route is available';
  if (deps.bridge && me !== null && job.printer.ownerTerminalId === me) {
    try {
      const r = await deps.bridge.print(refOf(job.printer), job.doc);
      if (r.ok) return { route: 'device' };
      error = r.error ?? 'The printer did not print';
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }
  const hub = await resolveHubClient(deps);
  if (hub) {
    try {
      await hub.print(job.id, job.printerId, job.doc);
      return { route: 'hub' };
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }
  return { error };
}

/** Print (or queue) one document for a printer. Safe to call again with the same id. */
export async function printOffline(
  job: Pick<PendingPrint, 'id' | 'outletId' | 'printer' | 'doc'>,
  deps: PrintDeps = {},
): Promise<PrintRoute> {
  if ((await done()).includes(job.id)) return 'device';
  const held = (await queuedPrints()).find((q) => q.id === job.id);
  if (held) return 'queued';
  const pending: PendingPrint = {
    ...job,
    printerId: job.printer.id,
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  const r = await attempt(pending, deps);
  if ('route' in r) {
    await markDone(job.id);
    return r.route;
  }
  await setQueue([...(await queuedPrints()), { ...pending, attempts: 1, lastError: r.error }]);
  return 'queued';
}

/** Try the queued jobs again, oldest first; returns how many left the queue. */
export async function flushPrintQueue(deps: PrintDeps = {}): Promise<number> {
  const queue = await queuedPrints();
  const left: PendingPrint[] = [];
  let sent = 0;
  for (const job of queue) {
    const r = await attempt(job, deps);
    if ('route' in r) {
      await markDone(job.id);
      sent += 1;
    } else {
      left.push({ ...job, attempts: job.attempts + 1, lastError: r.error });
    }
  }
  await setQueue(left);
  return sent;
}

/** The KOT of an offline send on every printer of its station. */
export async function printKotOffline(
  facts: SendFacts,
  kotNo: number,
  deps: PrintDeps = {},
): Promise<PrintRoute[]> {
  const routes: PrintRoute[] = [];
  for (const p of buildKotPrints(facts, kotNo)) {
    const printer = facts.bootstrap.printers.find((x) => x.id === p.printerId);
    if (!printer) continue;
    routes.push(
      await printOffline(
        {
          id: `kot:${facts.kotId}:${p.printerId}`,
          outletId: facts.order.outletId,
          printer,
          doc: renderKotDoc(p.data),
        },
        deps,
      ),
    );
  }
  return routes;
}

/** The facts a bill prints, from the order the till holds (integer paise, no floats). */
export function billData(
  order: OrderView,
  bootstrap: OutletBootstrap,
  opts: {
    invoiceNo: string | null;
    now: Date;
    paperMm: 58 | 80;
    copies?: number;
    reprint?: boolean;
  },
): BillPrintData {
  const a = bootstrap.outlet.address;
  const live = order.lines.filter((l) => l.status !== 'void' && l.qtyMilli > 0);
  return {
    outlet: {
      name: bootstrap.outlet.name,
      addressLines: a ? [a.line1, ...(a.line2 ? [a.line2] : []), `${a.city} ${a.pincode}`] : [],
      gstin: bootstrap.outlet.gstin,
      fssaiNo: bootstrap.outlet.fssaiNo,
    },
    orderNo: order.orderNo,
    orderType: order.type,
    tableNames: order.tableIds.flatMap(
      (id) => bootstrap.tables.find((t) => t.id === id)?.name ?? [],
    ),
    covers: order.covers,
    at: formatOutletTime(
      new Date(order.billedAt ?? opts.now.toISOString()),
      bootstrap.outlet.timezone,
    ),
    invoiceNo: opts.invoiceNo,
    customerName: order.customer?.name ?? null,
    placeOfSupply: a ? { state: a.state, code: gstinStateCode(bootstrap.outlet.gstin) } : null,
    lines: live.map((l) => ({
      name: l.name,
      qtyMilli: l.qtyMilli,
      unitPricePaise: BigInt(l.unitPricePaise),
      amountPaise: (BigInt(l.unitPricePaise) * BigInt(l.qtyMilli)) / 1000n,
      complimentary: l.complimentary,
      modifiers: l.modifiers.map((m) => m.name),
    })),
    subtotalPaise: BigInt(order.subtotalPaise),
    discounts: order.discounts.map((d) => ({
      name: d.name ?? 'Discount',
      amountPaise: BigInt(d.amountPaise),
    })),
    charges: order.charges
      .filter((c) => !c.waived)
      .map((c) => ({ name: c.name, amountPaise: BigInt(c.amountPaise) })),
    taxes: order.taxes.map((t) => ({
      component: t.component,
      rateBp: t.rateBp,
      taxPaise: BigInt(t.taxPaise),
      taxablePaise: BigInt(t.taxablePaise),
    })),
    roundOffPaise: BigInt(order.roundOffPaise),
    totalPaise: BigInt(order.totalPaise),
    paidPaise: BigInt(order.paidPaise),
    paperMm: opts.paperMm,
    copies: opts.copies ?? 1,
    watermark: opts.reprint ? 'REPRINT' : null,
  };
}

/** The bill on the terminal's bill printer (the first one that prints bills). */
export async function printBillOffline(
  order: OrderView,
  bootstrap: OutletBootstrap,
  opts: { invoiceNo: string | null; now: Date; reprints: number },
  deps: PrintDeps = {},
): Promise<PrintRoute | null> {
  const me = terminalStore.get()?.terminalId ?? null;
  const printers = bootstrap.printers.filter((p) => p.printsBills && p.archivedAt === null);
  const printer = printers.find((p) => p.ownerTerminalId === me) ?? printers[0];
  if (!printer) return null;
  const data = billData(order, bootstrap, {
    invoiceNo: opts.invoiceNo,
    now: opts.now,
    paperMm: printer.paperMm,
    reprint: opts.reprints > 0,
  });
  return printOffline(
    {
      id: `bill:${order.id}:${opts.reprints}`,
      outletId: order.outletId,
      printer,
      doc: renderBillDoc(data),
    },
    deps,
  );
}
